import dayjs from "dayjs";

import { currentFeatures, oids } from "@/configs/Settings";

const PAGE_SIZE = 350;

export const WEARABLE_STEPS_CODES = [
  "http://loinc.org|55423-8",
  "55423-8",
] as const;

export const WEARABLE_SCREEN_TIME_CODES = ["screen-time"] as const;

export const WEARABLE_GENERAL_METRIC_CODES = [
  "http://loinc.org|8867-4",
  "8867-4",
  "http://loinc.org|85354-9",
  "85354-9",
  "http://loinc.org|9052-2",
  "9052-2",
  "http://loinc.org|8999-5",
  "8999-5",
  "http://loinc.org|2339-0",
  "2339-0",
] as const;

export const WEARABLE_SLEEP_METRIC_CODES = [
  "http://loinc.org|93832-4",
  "93832-4",
  "http://loinc.org|93830-8",
  "93830-8",
  "http://loinc.org|93831-6",
  "93831-6",
  "http://loinc.org|93829-0",
  "93829-0",
  "http://loinc.org|93828-2",
  "93828-2",
  "http://loinc.org|96634-8",
  "96634-8",
  "http://loinc.org|59408-5",
  "59408-5",
  "http://loinc.org|81264-4",
  "81264-4",
] as const;

const SPO2_CODES = new Set(["59408-5", "http://loinc.org|59408-5"]);

export interface WearableObservationSearchRequest {
  startDate: string;
  endDate: string;
  code?: string;
}

function chunkDateRange(
  startKey: string,
  endKey: string,
  chunkDays: number,
): Array<{ start: string; end: string }> {
  const start = dayjs(startKey);
  const end = dayjs(endKey);
  if (!start.isValid() || !end.isValid() || start.isAfter(end, "day")) {
    return [{ start: startKey, end: endKey }];
  }

  const windows: Array<{ start: string; end: string }> = [];
  let cursor = start;
  while (cursor.isBefore(end, "day") || cursor.isSame(end, "day")) {
    const windowEnd = cursor.add(chunkDays - 1, "day");
    const capped = windowEnd.isAfter(end, "day") ? end : windowEnd;
    windows.push({
      start: cursor.format("YYYY-MM-DD"),
      end: capped.format("YYYY-MM-DD"),
    });
    cursor = capped.add(1, "day");
  }
  return windows.length > 0 ? windows : [{ start: startKey, end: endKey }];
}


export function resolvePatientFhirLogicalId(identifier: string): string {
  const trimmed = identifier.trim();
  return currentFeatures.defaultApp ? `${oids.cpf}-${trimmed}` : trimmed;
}

/** Monta URL relativa GET Observation (padrão PatientSummary no EHRRunner). */
export function buildWearableObservationSearchUrl(
  patientIdentifier: string,
  params: WearableObservationSearchRequest,
): string {
  const { startDate, endDate, code } = params;
  const patientFhirLogicalId = resolvePatientFhirLogicalId(patientIdentifier);
  let url = `Observation?subject=Patient/${patientFhirLogicalId}&status=final&_sort=-_lastUpdated&_count=${PAGE_SIZE}`;
  if (code) url += `&code=${encodeURIComponent(code)}`;
  if (startDate) url += `&date=ge${startDate}`;
  if (endDate) url += `&date=le${endDate}`;
  return url;
}

/**
 * Lista de GETs do bundle MHD para o período wearable.
 * Várias entries no bundle, porém **um único POST** no MHD.
 */
export function buildWearableObservationSearchRequests(params: {
  patientId: string;
  periodStartKey: string;
  periodEndKey: string;
  rangeDays: number;
}): WearableObservationSearchRequest[] {
  const { patientId, periodStartKey, periodEndKey, rangeDays } = params;
  const requests: WearableObservationSearchRequest[] = [];

  const pushWindow = (startDate: string, endDate: string, code?: string) => {
    requests.push({ startDate, endDate, code });
  };

  const dedicatedCodes = [
    ...WEARABLE_STEPS_CODES,
    ...WEARABLE_SCREEN_TIME_CODES,
    ...WEARABLE_GENERAL_METRIC_CODES,
  ];

  for (const code of dedicatedCodes) {
    pushWindow(periodStartKey, periodEndKey, code);
  }

  const sleepStartKey = dayjs(periodStartKey)
    .subtract(1, "day")
    .format("YYYY-MM-DD");

  for (const code of WEARABLE_SLEEP_METRIC_CODES) {
    const chunkDays = SPO2_CODES.has(code) ? 7 : 15;
    for (const { start, end } of chunkDateRange(
      sleepStartKey,
      periodEndKey,
      chunkDays,
    )) {
      pushWindow(start, end, code);
    }
  }

  if (rangeDays <= 15) {
    pushWindow(periodStartKey, periodEndKey);
    return requests;
  }

  const recentStart = dayjs(periodEndKey)
    .subtract(14, "day")
    .format("YYYY-MM-DD");
  const olderEnd = dayjs(recentStart).subtract(1, "day").format("YYYY-MM-DD");
  pushWindow(recentStart, periodEndKey);
  pushWindow(periodStartKey, olderEnd);

  return requests;
}
